import { EditOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { useSaveNoteMutation } from "../../../../App/Queries/NoteQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { ICandidateNote } from "../../../../App/Typings/CandidateProfile";
import { CandidateContext } from "../../../CandidateProfile";
import { EditNote } from "./EditNote";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";

export function NoteDisplay({ note }: { note: ICandidateNote }) {
  const { candidate } = useContext(CandidateContext);
  const user = useCurrentUser();
  const { displayError, displaySuccess } = useNotification();

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const saveNoteMutation = useSaveNoteMutation();

  /**
   * @description cancels an edit on a note
   *
   * @param {string} cancelNoteId
   */
  const handleCancelEdit = (cancelNoteId: string) => {
    setIsEditing(false);
  };

  /**
   * @description saves any edits to a note that were made
   *
   * @param {ICandidateNote} note
   * @returns {Promise<void>}
   */
  const handleSaveEdit = async (note: ICandidateNote): Promise<void> => {
    if (note.note.length <= 0 || !candidate.id) {
      return;
    }
    if (note.note.length > 1500) {
      displayError("Notes must be less than 1500 characters. Please edit your note and try again.");
      return;
    }
    saveNoteMutation.mutate(
      {
        candidateId: candidate.id,
        noteId: note.id,
        note: note.note,
        userId: user.id,
      },
      {
        onError: () => displayError("There was an error saving your note. Please try again!"),
        onSuccess: () => {
          displaySuccess("Your note was successfully edited!");
          setIsEditing(false);
        },
      }
    );
  };

  /**
   * @description toggles editable mode for a given note
   * @param noteId
   */
  const handleToggleNoteEdit = (noteId: string) => {
    setIsEditing(true);
  };

  return isEditing ? (
    <EditNote cancelEditMode={handleCancelEdit} note={note} saveEdit={handleSaveEdit}></EditNote>
  ) : (
    <>
      <Typography id={`note-${note.id}`} data-test={`note-${note.id}`} sx={{ whiteSpace: "pre-line" }}>
        {note.note}
      </Typography>
      {user.id === note.userId && (
        <Box sx={{ position: "absolute", top: "8px", right: "16px" }}>
          <IconButton
            data-test={`note-toggleeditnote-${note.id}`}
            onClick={() => handleToggleNoteEdit(note.id)}
            sx={{
              backgroundColor: "primary.contrastText",
              color: "primary.main",
              boxShadow: "none",
              width: 40,
              height: 40,
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </Box>
      )}
    </>
  );
}
